import React, { useState } from 'react';
import { Card, CardMedia, Stack, Box, CircularProgress } from '@mui/material';
import { Button, Typography } from 'components/index';
import Dropzone from '../../Dropzone';
import { FilesListItem } from '../../file-upload/FileUploadDialog';
import { FileUploadStatus } from '../../file-upload/fileUpload.type';
import { toast } from 'react-toastify';
import { formatBytes, isHeicFile } from '../../../common/utils/common.util';
import { deleteCoverImage, getFileUploadUrl, uploadCoverImage } from '../../project/project.api';
import { handleError } from '../../../common/utils/util.errors';
import { ProjectSummary } from '../../project/project.type';
import PreviewImage from './PreviewImage';

interface CoverImageProps {
  project: ProjectSummary;
}

const filesToAccept = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
};
const HEIC_QUALITY = 0.8;

const getHeicTo = async () => {
  let heicTo: ((options: { blob: Blob; type: string; quality?: number }) => Promise<Blob>) | undefined;
  if (typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined') {
    try {
      ({ heicTo } = await import('heic-to/next'));
    } catch (error) {
      console.warn('Falling back to main-thread HEIC conversion:', error);
    }
  }
  if (!heicTo) {
    ({ heicTo } = await import('heic-to'));
  }
  return heicTo;
};

const CoverImage: React.FC<CoverImageProps> = ({ project }) => {
  const [coverImageBlobName, setCoverImageBlobName] = useState<string | null>(project.coverImage?.blobName || null);
  const [previewBlobName, setPreviewBlobName] = useState<string | null>(project.previewCoverImage?.blobName || null);

  const isCoverImagePresent = Boolean(previewBlobName);
  const [editMode, setEditMode] = useState(!isCoverImagePresent);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConvertingImage, setIsConvertingImage] = useState(false);

  const [fileItem, setFileItem] = useState<FilesListItem | null>(null);

  // React.useEffect(() => {
  //   const fetchBlobUrl = async () => {
  //     if (previewBlobName == null) {
  //       setPreviewBlobUrl(null);
  //       return;
  //     }
  //     try {
  //       const url = await getFileUploadUrl(previewBlobName);
  //       setPreviewBlobUrl(url);
  //       setIsImageLoaded(false);
  //     } catch (error) {
  //       handleError(error);
  //       setPreviewBlobUrl(null);
  //     }
  //   };
  //   fetchBlobUrl();
  // }, [previewBlobName]);

  const handleDrop = async (files: File[]) => {
    if (files.length !== 1) {
      toast.error('Only one file can be uploaded at a time.', { autoClose: 2000 });
      return;
    }
    const file = files[0];
    const isHeic = isHeicFile(file);
    let previewUrl: string | undefined;

    if (isHeic) {
      try {
        setIsConvertingImage(true);
        const heicTo = await getHeicTo();

        const convertedBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: HEIC_QUALITY,
        });

        const convertedFile = new File([convertedBlob], file.name.replace(/\.heic$/i, '.jpg'), {
          type: 'image/jpeg',
        });

        const previewUrl = URL.createObjectURL(convertedFile);

        setFileItem({
          file: convertedFile,
          previewUrl,
          status: FileUploadStatus.Idle,
          errors: [],
          progress: 0,
        });
      } catch (err) {
        console.error('Failed to convert HEIC to JPEG', err);
        previewUrl = undefined;
      } finally {
        setIsConvertingImage(false);
      }
    } else {
      previewUrl = URL.createObjectURL(file);
      const fileListItem = {
        file,
        status: FileUploadStatus.Idle,
        errors: [],
        progress: 0,
        previewUrl,
      };
      setFileItem(fileListItem);
    }
  };

  const removeFile = () => {
    if (fileItem?.previewUrl) {
      URL.revokeObjectURL(fileItem.previewUrl);
    }
    setFileItem(null);
  };

  const handleUpload = async () => {
    if (!fileItem) return;
    try {
      const result = await uploadCoverImage(
        fileItem.file,
        (name, status, errors, progress) => {
          setFileItem({ ...fileItem, status, errors, progress });
        },
        project.projectId,
      );
      toast.success('Cover image uploaded successfully.');
      setPreviewBlobName(result.previewCoverImageBlobName);
      setCoverImageBlobName(result.coverImageBlobName);
      setFileItem(null);
      setEditMode(false);
    } catch (error) {
      handleError(error);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteCoverImage(project.projectId);
      toast.success('Cover image deleted successfully.');
      setPreviewBlobName(null);
      setCoverImageBlobName(null);
      setEditMode(true);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const showOriginalImage = async () => {
    if (coverImageBlobName) {
      const url = await getFileUploadUrl(coverImageBlobName);
      window.open(url, '_blank');
    } else {
      toast.error('No cover image available.');
    }
  };

  return (
    <Card
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        '&:hover .image-overlay': {
          opacity: 1,
        },
      }}
    >
      {editMode ? (
        isConvertingImage ? (
          <Stack alignItems='center' justifyContent='center' height='100%'>
            <CircularProgress />
            <Typography variant='body2' mt={2}>
              Converting image to JPEG...
            </Typography>
          </Stack>
        ) : fileItem ? (
          <>
            {fileItem.previewUrl ? (
              <CardMedia
                component='img'
                height='100%'
                image={fileItem.previewUrl}
                alt='preview'
                sx={{ ...mediaStyles, border: '2px dashed #000' }}
              />
            ) : (
              <Stack p={2}>
                <Typography variant='body2' fontWeight='bold'>
                  {fileItem.file.name}
                </Typography>
                <Typography variant='caption'>{formatBytes(fileItem.file.size)}</Typography>
              </Stack>
            )}
            <Box className='image-overlay' sx={{ ...overlayStyles, opacity: 1 }}>
              <Stack direction={'row'} alignItems='center' justifyContent='flex-end' width='100%' px={2}>
                <Button color='error' variant='standard' sx={{ mt: 1 }} onClick={removeFile} disabled={!fileItem}>
                  Remove
                </Button>
                <Button
                  color='primary'
                  variant='standard'
                  sx={{ mt: 1 }}
                  onClick={handleUpload}
                  disabled={!fileItem}
                  loading={fileItem?.status === FileUploadStatus.Uploading || fileItem?.status === FileUploadStatus.Parsing}
                >
                  Upload
                </Button>
              </Stack>
            </Box>
          </>
        ) : (
          <Dropzone onDrop={handleDrop} accept={filesToAccept} additionalStyles={dropzoneStyles} fileTitle='Cover Image' />
        )
      ) : (
        <>
          <PreviewImage blobName={previewBlobName} onClick={showOriginalImage} alt='cover image' sx={{ cursor: 'pointer' }} />

          <Box className='image-overlay' sx={overlayStyles}>
            <Button variant='standard' color='error' onClick={handleDelete} loading={isDeleting}>
              Delete
            </Button>
            <Button variant='standard' onClick={() => setEditMode(true)}>
              Replace
            </Button>
          </Box>
        </>
      )}
    </Card>
  );
};

export default CoverImage;

const mediaStyles = { width: '100%', height: '100%', objectFit: 'cover', margin: 0 };

const overlayStyles = {
  position: 'absolute',
  bottom: 0,
  height: '30%',
  width: '100%',
  background: 'linear-gradient(to top, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0))',
  color: 'white',
  py: 2,
  px: 2,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'end',
  transition: 'opacity 0.3s ease-in-out',
  opacity: 0,
};

const dropzoneStyles = { flex: 1, margin: '16px', width: 'unset' };
